package com.elan.edubridge.data.firebase

import com.elan.edubridge.data.firebase.externals.connectAuthEmulator
import com.elan.edubridge.data.firebase.externals.connectFirestoreEmulator
import com.elan.edubridge.data.firebase.externals.connectFunctionsEmulator
import com.elan.edubridge.data.firebase.externals.connectStorageEmulator
import com.elan.edubridge.data.firebase.externals.getAuth
import com.elan.edubridge.data.firebase.externals.getFirestore
import com.elan.edubridge.data.firebase.externals.getFunctions
import com.elan.edubridge.data.firebase.externals.getStorage
import com.elan.edubridge.data.firebase.externals.initializeApp
import com.elan.edubridge.shared.data.firebase.FirebaseNaming
import com.elan.edubridge.shared.di.SharedGraph
import kotlinx.browser.window
import org.w3c.dom.url.URLSearchParams

/**
 * `SharedGraph.install(...)`을 호출하는 유일한 진입점(§S4).
 *
 * Firebase 웹 설정은 `apps/web/src/jsMain/resources/firebase-config.js`가 `window.__FIREBASE_CONFIG__`로
 * 미리 주입해 둔다(그 파일은 lane C 소유 — 여기서는 읽기만 한다). Firestore는 반드시 named DB
 * `edubridge`를 지정하고(`FirebaseNaming.FIRESTORE_DATABASE_ID`), Functions는 asia-northeast3로
 * 고정한다(`FirebaseNaming.FUNCTIONS_REGION`) — 이후 모든 콜러블이 `getFunctions`에 저장된 이
 * 리전을 그대로 물려받으므로 호출부마다 리전을 반복 지정할 필요가 없다.
 *
 * **호출 순서 주의**: `SharedGraph.install()`은 첫 `create*` 호출 이전에 한 번만 가능하다
 * (`shared/.../di/SharedGraph.kt` 참고, 두 번째 호출이거나 이미 `create*`가 불린 뒤라면
 * `IllegalStateException`). `Main.kt`가 다른 `SharedGraph.create*()`를 부르기 전에 이 함수를
 * 먼저 호출해야 한다 — 그 연결은 통합 시점에 붙이며 이 lane의 범위 밖이다.
 *
 * **"웹·앱은 DB 기반으로 동작해야 한다" — InMemory 폴백 원천 차단(사용자 요구 반영)**:
 * `SharedGraph`는 `install()`이 아예 호출되지 않았을 때만 조용히 InMemory로 폴백한다
 * (`SharedGraph.kt` 참고). 그 조용한 폴백이 프로덕션에서 발동하는 유일한 경로는 이 함수가
 * "실패했는데 그냥 반환"하는 것이다 — 그래서 여기서는 설정이 없거나 `initializeApp`이 실패하면
 * **예외를 던진다.** 호출부(`Main.kt`, 통합 시점)가 이 예외를 잡아 명시적 오류 화면을 띄우는 것을
 * 전제로 한다 — 데모 데이터로 조용히 넘어가는 경로를 남기지 않는다.
 */
fun installWebGraph() {
    val config = window.asDynamic().__FIREBASE_CONFIG__
    check(jsTypeOf(config) == "object" && config != null) {
        "window.__FIREBASE_CONFIG__가 없습니다. firebase-config.js가 로드됐는지 확인하세요."
    }
    // initializeApp이 실패하면(예: config 형태가 잘못됨) 여기서 잡지 않고 그대로 던진다 —
    // 조용한 InMemory 폴백을 만들지 않기 위한 의도적 선택.
    val app = initializeApp(config)
    val auth = getAuth(app)
    val firestore = getFirestore(app, FirebaseNaming.FIRESTORE_DATABASE_ID)
    val functions = getFunctions(app, FirebaseNaming.FUNCTIONS_REGION)
    // 기본 Firebase 설정은 EduMint 버킷을 가리킨다. EduBridge 규칙이 배포된 전용 버킷을 명시한다.
    val storage = getStorage(app, "gs://${FirebaseNaming.STORAGE_BUCKET}")

    if (isEmulatorOptIn()) {
        connectAuthEmulator(auth, "http://127.0.0.1:9099")
        connectFirestoreEmulator(firestore, "127.0.0.1", 8080)
        connectFunctionsEmulator(functions, "127.0.0.1", 5001)
        connectStorageEmulator(storage, "127.0.0.1", 9199)
    }

    SharedGraph.install(
        accountDataSource = FirebaseAccountDataSource(auth, firestore, functions),
        catalogDataSource = FirebaseEduConnectDataSource(auth, firestore, functions),
        commandDataSource = FirebaseAcademyCommandDataSource(functions, firestore),
    )
    // Google 로그인 자체는 shared 계약이 처리하고, WebAuth는 웹 전용 플랫폼 관리자 부트스트랩에
    // 필요한 Functions 핸들만 받는다 — WebAuth.kt 참고.
    WebAuth.configure(functions)
    AttachmentStagingUploader.configure(auth, storage, functions)
    AcademySubscriptionReader.configure(firestore)

    // S9 E2E·운영 진단에서 InMemory가 아니라 실 DB 모드로 떴다는 것을 한 줄로 즉시 확인한다.
    console.info(
        "[EduBridge] datasource=firebase db=${FirebaseNaming.FIRESTORE_DATABASE_ID} region=${FirebaseNaming.FUNCTIONS_REGION}",
    )
}

/**
 * 에뮬레이터 연결은 명시적 opt-in일 때만 켠다 — localhost/127.0.0.1(로컬 개발) 또는 그 호스트에서의
 * URL 쿼리 `?emulator=1`. 프로덕션 도메인(`elan-edubridge.web.app` 등)은 호스트명 판정에서 바로
 * `false`로 끝나 쿼리 문자열을 읽지도 않는다 — 리뷰 B2: 이전 구현은 쿼리 검사가 호스트 판정과
 * 무관한 `return` 경로였어서 프로덕션 도메인에 `?emulator=1`을 붙이면 그대로 통과했다(KDoc의 주장과
 * 실제 동작이 어긋났던 지점). `contains` 부분 문자열 매칭 대신 `URLSearchParams`로 정확히 비교한다.
 */
private fun isEmulatorOptIn(): Boolean {
    val hostname = window.location.hostname
    val isLocalHost = hostname == "localhost" || hostname == "127.0.0.1"
    if (!isLocalHost) return false
    val emulatorParam = URLSearchParams(window.location.search).get("emulator")
    return isLocalHost || emulatorParam == "1"
}
